import { useQuery } from "@tanstack/react-query";

import { getMyPermissions } from "@/api/portal";
import { useIAMAuth } from "@/auth/iam";

/**
 * Server-resolved permissions for the SPA.
 *
 * ⚠️ The SPA cannot learn its permissions from the token. IAM does not embed
 * permission VALUES in an access token (only an advisory `permissions_count`),
 * so `useIAMAuth().permissions` — decoded from the JWT — is EMPTY for every
 * user, always. Gating UI on it hid every authoring page from everyone against
 * real IAM. This hook resolves them from `GET /api/v1/portal/me/permissions`
 * instead, the one surface that knows the truth.
 *
 * The short strings the app gates on (`catalog:read`) map to IAM's 4-part keys
 * (`smart-reports-catalog:catalog:content:read`). The map lives here, and the
 * comparison is case-insensitive — IAM stores at least one literal mixed-case
 * (`iam:Users:Management:admin`), so a case-sensitive check would silently miss.
 */
const KEY_MAP = {
  "catalog:read": "smart-reports-catalog:catalog:content:read",
  "catalog:author": "smart-reports-catalog:catalog:content:author",
  "audit:read": "smart-reports-audit:audit:events:read",
  // The Client Portal permission. Every portal user-admin, approvals and LIS-message
  // route in onboarding-api is `require_permission_resolved(CLIENTS_MANAGE)`, so this
  // is the key the SPA must gate the portal on, not the token's `role` claim (which
  // real IAM does not set for an invited portal user).
  "clients:manage": "smart-reports-config:clients:tenants:manage",
  // Gate B M1 S7, the lab portal Reports page. The key the BFF's /api/v1/portal/reports
  // routes enforce (onboarding_api.permissions.REPORTS_CLINICAL_READ): the request
  // explorer's clinical read, granted to a lab's doctors through its own
  // GG-SmartReports-<lab>-Clinician group (Aghiad, docs#182 note 10322).
  "reports:clinical:read": "smart-reports-insights:requests:clinical:read",
  // Gate B white-labelling (contract 4.7, onboarding_api.permissions). Medicus staff brand
  // any organisation, a lab's client admin its own and every one under it (D10), and a lab
  // doctor manages only their own signature. Seeded and granted by Aghiad.
  "branding:admin": "smart-reports-config:branding:organizations:admin",
  "branding:manage": "smart-reports-config:branding:organizations:manage",
  "branding:signature": "smart-reports-config:branding:signature:manage-own",
} as const;

/**
 * The permissions the UI can gate on. Deriving the type from `KEY_MAP` keys
 * makes an unmapped `perm` a COMPILE error, not a silent fail-closed. Without
 * this, `<RequirePermission perm="clients:admin">` would map to a short string
 * that never matches the resolved 4-part keys, hiding a working page from
 * everyone with no error — the exact silent-auth class this whole path fixes.
 * To gate on a new permission, add its 4-part key here first.
 */
export type GatedPermission = keyof typeof KEY_MAP;

export interface ResolvedPermissions {
  /** True once the resolve has settled (success or failure). */
  isReady: boolean;
  /** True if the resolve failed — the caller decides whether to fail closed. */
  isError: boolean;
  has: (perm: GatedPermission) => boolean;
}

export function useResolvedPermissions(): ResolvedPermissions {
  const { isAuthenticated } = useIAMAuth();
  const query = useQuery({
    queryKey: ["me-permissions"],
    queryFn: getMyPermissions,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // permissions change rarely; don't refetch per guard
    retry: false,
  });

  const granted = new Set((query.data?.permissions ?? []).map((k) => k.toLowerCase()));

  return {
    isReady: !query.isPending,
    isError: query.isError,
    has: (perm: GatedPermission) => granted.has(KEY_MAP[perm].toLowerCase()),
  };
}
